/**
 * Multi-region phone helpers backed by libphonenumber-js/max.
 *
 * Storage contract matches backend: E.164 digits without '+'.
 * Supported regions: BR, PT, ES.
 * Tenant-first parse (aligned with backend phone_normalize): prefer valid
 * national numbers in defaultRegion; only promote ambiguous CC shapes
 * (e.g. BR DDD 34 vs ES +34) when national parse fails and +digits is valid.
 */

// Deep path (not the bare specifier) so the module graph also loads on a plain
// static server such as Live Server on :5500 — browsers cannot resolve bare
// specifiers without an import map. Vite and `vite build` resolve it too.
import {
  AsYouType,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "../../../node_modules/libphonenumber-js/max/index.js";

export const SUPPORTED_PHONE_REGIONS = Object.freeze(["BR", "PT", "ES"]);

const REGION_CALLING_CODES = Object.freeze({
  BR: "55",
  PT: "351",
  ES: "34",
});

/**
 * National prefixes that collide with another supported region's calling code.
 * For these, prefer international display so BR DDD 34 is not mistaken for ES +34.
 * Key = tenant region; value = list of ambiguous national prefixes.
 */
const AMBIGUOUS_NATIONAL_PREFIXES = Object.freeze({
  BR: Object.freeze(["34"]),
});

/**
 * @param {string} [region]
 * @returns {"BR"|"PT"|"ES"}
 */
export function resolvePhoneRegion(region = "BR") {
  const resolved = String(region || "BR")
    .trim()
    .toUpperCase();
  if (SUPPORTED_PHONE_REGIONS.includes(resolved)) {
    return /** @type {"BR"|"PT"|"ES"} */ (resolved);
  }
  throw new Error(`Unsupported phone default region: ${resolved}`);
}

/**
 * @param {string} phone
 * @returns {string}
 */
export function stripPhoneDigits(phone) {
  return String(phone || "").replace(/\D/g, "");
}

/**
 * @param {string} digits
 * @returns {string|null} ISO region when digits look like supported E.164
 */
function detectExplicitRegion(digits) {
  if (digits.startsWith("351") && digits.length === 12) return "PT";
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
    return "BR";
  }
  if (digits.startsWith("34") && digits.length === 11) return "ES";
  return null;
}

/**
 * @param {string} digits
 * @param {"BR"|"PT"|"ES"} region
 * @returns {boolean}
 */
function looksLikeRegionE164(digits, region) {
  return detectExplicitRegion(digits) === region;
}

/**
 * Tenant-first parse shared by normalize / isValid / format.
 * @param {string} phone
 * @param {string} [defaultRegion="BR"]
 * @returns {import("libphonenumber-js").PhoneNumber|null}
 */
function parseTenantFirst(phone, defaultRegion = "BR") {
  const original = String(phone || "").trim();
  if (!original) return null;

  let digits = stripPhoneDigits(original);
  const hadIntlPrefix = original.startsWith("+") || digits.startsWith("00");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (!digits) return null;

  let region;
  try {
    region = resolvePhoneRegion(defaultRegion);
  } catch {
    return null;
  }

  if (hadIntlPrefix) {
    const parsed = parsePhoneNumberFromString(`+${digits}`);
    return parsed && parsed.isValid() ? parsed : null;
  }

  if (looksLikeRegionE164(digits, region)) {
    const parsed = parsePhoneNumberFromString(`+${digits}`);
    return parsed && parsed.isValid() ? parsed : null;
  }

  const national = parsePhoneNumberFromString(digits, region);
  if (national && national.isValid()) return national;

  if (detectExplicitRegion(digits)) {
    const intl = parsePhoneNumberFromString(`+${digits}`);
    if (intl && intl.isValid()) return intl;
  }

  return null;
}

/**
 * Normalize to E.164 digits without plus. Returns "" when empty/incomplete.
 * Invalid complete numbers return best-effort digits (no invented foreign CC).
 *
 * @param {string} phone
 * @param {string} [defaultRegion="BR"]
 * @returns {string}
 */
export function normalizePhone(phone, defaultRegion = "BR") {
  if (!phone) return "";
  let digits = stripPhoneDigits(phone);
  if (!digits) return "";

  if (digits.startsWith("00")) {
    digits = digits.slice(2);
  }
  if (!digits) return "";

  try {
    resolvePhoneRegion(defaultRegion);
  } catch {
    return "";
  }

  const parsed = parseTenantFirst(phone, defaultRegion);
  if (!parsed) {
    return digits;
  }
  return parsed.format("E.164").replace(/^\+/, "");
}

/**
 * Strict validity check for persistence / WhatsApp actions.
 * @param {string} phone
 * @param {string} [defaultRegion="BR"]
 * @returns {boolean}
 */
export function isValidPhone(phone, defaultRegion = "BR") {
  if (!phone) return false;
  try {
    resolvePhoneRegion(defaultRegion);
  } catch {
    return false;
  }
  return Boolean(parseTenantFirst(phone, defaultRegion));
}

/**
 * As-you-type formatting for inputs.
 * @param {string} value
 * @param {string} [region="BR"]
 * @returns {string}
 */
export function formatAsYouType(value, region = "BR") {
  if (!value) return "";
  let resolved;
  try {
    resolved = resolvePhoneRegion(region);
  } catch {
    resolved = "BR";
  }
  const digits = stripPhoneDigits(value);
  if (!digits) return "";

  // Prefer national formatting when user types national digits.
  const formatter = new AsYouType(resolved);
  // If value already carries country code matching region, format international-ish.
  const cc = REGION_CALLING_CODES[resolved];
  if (digits.startsWith(cc) && digits.length > cc.length + 2) {
    return formatter.input("+" + digits);
  }
  return formatter.input(digits);
}

/**
 * @param {import("libphonenumber-js").PhoneNumber} parsed
 * @param {"BR"|"PT"|"ES"} resolved
 * @returns {string}
 */
function formatParsedForDisplay(parsed, resolved) {
  // National display for the tenant region; international otherwise.
  // Ambiguous national prefixes (e.g. BR DDD 34 vs ES +34) use international
  // so the table/UI is not confused with another supported country.
  if (parsed.country === resolved) {
    const national = String(parsed.nationalNumber || "");
    const ambiguous = AMBIGUOUS_NATIONAL_PREFIXES[resolved] || [];
    if (ambiguous.some((prefix) => national.startsWith(prefix))) {
      return parsed.formatInternational();
    }
    return parsed.formatNational();
  }
  return parsed.formatInternational();
}

/**
 * Display formatting for stored numbers.
 * @param {string} value
 * @param {string} [region="BR"]
 * @returns {string}
 */
export function formatPhoneForRegion(value, region = "BR") {
  if (!value) return "";
  let resolved;
  try {
    resolved = resolvePhoneRegion(region);
  } catch {
    resolved = "BR";
  }

  let digits = stripPhoneDigits(value);
  if (!digits) return "";
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (!digits) return "";

  const parsed = parseTenantFirst(value, resolved);
  if (parsed) {
    return formatParsedForDisplay(parsed, resolved);
  }

  // Display recovery when explicit +CC failed validation (e.g. "+34" + BR DDD 34):
  // retry as tenant-national digits so the funnel table still disambiguates.
  const nationalRecovery = parsePhoneNumberFromString(digits, resolved);
  if (nationalRecovery && nationalRecovery.isValid()) {
    return formatParsedForDisplay(nationalRecovery, resolved);
  }

  return formatAsYouType(value, resolved);
}

/**
 * @param {string} [region]
 * @returns {string} e.g. "+55"
 */
export function getDialPrefix(region = "BR") {
  let resolved;
  try {
    resolved = resolvePhoneRegion(region);
  } catch {
    resolved = "BR";
  }
  try {
    return `+${getCountryCallingCode(resolved)}`;
  } catch {
    return `+${REGION_CALLING_CODES[resolved] || "55"}`;
  }
}

/**
 * Placeholder hint for inputs.
 * @param {string} [region="BR"]
 * @returns {string}
 */
export function getPhonePlaceholder(region = "BR") {
  let resolved;
  try {
    resolved = resolvePhoneRegion(region);
  } catch {
    resolved = "BR";
  }
  if (resolved === "PT") return "912 345 678";
  if (resolved === "ES") return "612 34 56 78";
  return "(11) 98765-4321";
}

/**
 * Legacy national candidates for local dedup (mirrors backend lookup).
 * @param {string} phone
 * @param {string} [defaultRegion="BR"]
 * @returns {string[]}
 */
export function legacyLookupCandidates(phone, defaultRegion = "BR") {
  const canonical = normalizePhone(phone, defaultRegion);
  if (!canonical) return [];
  const candidates = [canonical];
  for (const [region, cc] of Object.entries(REGION_CALLING_CODES)) {
    if (!canonical.startsWith(cc)) continue;
    const national = canonical.slice(cc.length);
    if (!national || candidates.includes(national)) break;
    if (region === "BR" && (canonical.length === 12 || canonical.length === 13)) {
      candidates.push(national);
    } else if (region === "PT" && canonical.length === 12) {
      candidates.push(national);
    } else if (region === "ES" && canonical.length === 11) {
      candidates.push(national);
    }
    break;
  }
  return candidates;
}

/**
 * Generate a synthetic display phone for test fills (not guaranteed unique).
 * @param {string} [region="BR"]
 * @returns {string}
 */
export function generateRandomPhone(region = "BR") {
  let resolved;
  try {
    resolved = resolvePhoneRegion(region);
  } catch {
    resolved = "BR";
  }
  if (resolved === "PT") {
    const rest = String(Math.floor(Math.random() * 1e8)).padStart(8, "0");
    return `9${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5)}`;
  }
  if (resolved === "ES") {
    const rest = String(Math.floor(Math.random() * 1e8)).padStart(8, "0");
    return `6${rest.slice(0, 2)} ${rest.slice(2, 4)} ${rest.slice(4, 6)} ${rest.slice(6)}`;
  }
  const areaCode = String(Math.floor(Math.random() * 89) + 11).padStart(2, "0");
  const firstPart = String(Math.floor(Math.random() * 10000)).padStart(4, "0");
  const secondPart = String(Math.floor(Math.random() * 10000)).padStart(4, "0");
  return `(${areaCode}) 9 ${firstPart}-${secondPart}`;
}

/**
 * Wire as-you-type formatting on an input.
 * @param {HTMLInputElement|string} inputOrId
 * @param {{ region?: string }} [options]
 */
export function setupPhoneFormatting(inputOrId = "phone", options = {}) {
  const phoneInput = typeof inputOrId === "string" ? document.getElementById(inputOrId) : inputOrId;
  if (!phoneInput) return;

  let region = "BR";
  try {
    region = resolvePhoneRegion(
      options.region ||
        phoneInput.dataset.phoneRegion ||
        (typeof window !== "undefined" && window.__AURIS_PHONE_REGION__) ||
        "BR"
    );
  } catch {
    region = "BR";
  }

  phoneInput.dataset.phoneRegion = region;
  phoneInput.removeAttribute("maxlength");

  phoneInput.addEventListener("input", (e) => {
    const resolved = e.target.dataset.phoneRegion || region || "BR";
    e.target.value = formatAsYouType(e.target.value, resolved);
  });

  phoneInput.addEventListener("keypress", (e) => {
    const char = String.fromCharCode(e.which);
    if (!/[0-9+\s()-]/.test(char)) {
      e.preventDefault();
    }
  });
}
